import Popup from '../../../popup/Popup'
import PopupSection from '../../../popup/PopupSection'
import PopupSectionRow from '../../../popup/PopupSectionRow'
import { ReactNode, useState } from 'react'
import {
  buildRemoveParticipantData,
  changeParticipantAdminStatus,
  changeParticipantColor,
  transferOwnership,
  updateGroupOnServer
} from '../../../../services/api/group'
import Logger from '../../../../services/Logger'
import {
  EventDoc,
  GroupDoc,
  GroupParticipant,
  GroupParticipantType,
  ParticipantDataDoc
} from '../../../../types/api/changes'
import { Model } from '../../../../types/model'
import ColorsPopup from '../../ColorsPopup'
import { theme } from 'theme'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { GENERIC_ERROR } from 'utils/strings'
import { useConnection } from 'components/providers/ConnectionProvider'
import {
  amountOfAdminsInGroup,
  featureToMinimumTierNeeded
} from 'utils/api/groups'

import { t } from 'i18next'
import { isOwnerForGroup } from 'services/db/utils/GroupUtils'
import { Tier } from 'utils/api/accounts'
import { parseErrorToMessage } from 'components/popups/eventInfo/EventPopup'
import { IEventDetails } from 'hooks/useEventDetails'
import { Divider, MenuItem } from '@mui/material'
import { TierFeatures } from 'utils/api/group_state'
import { isConvertedGroup } from 'utils/groups'
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog'
import { APP_COLOR } from 'utils/colors'
import { isTrulyEmpty } from 'utils/object'

interface ParticipantsActionsPopupContentProps {
  participant: GroupParticipant
  participantId: string
  setLoading: (b: boolean) => void
  setIsOpen: (b: boolean) => void
  group: Model<GroupDoc> | GroupDoc
  setError: (error: string) => void
  actionsOnEvent?: boolean
  eventHook?: IEventDetails
  participantInfo?: Model<ParticipantDataDoc>
}

export function ParticipantsActionsPopupContent(
  props: ParticipantsActionsPopupContentProps
) {
  const canTransferOwnership =
    props.group.OwnerID === LocalStorage.get(LocalStorageKey.USER_ID) &&
    props.participantId !== LocalStorage.get(LocalStorageKey.USER_ID) &&
    !isConvertedGroup(props.group)
  return (
    <>
      {props.actionsOnEvent ? (
        <div style={{ margin: 8, padding: 4 }}>
          <MenuItem>
            <PopupSectionRow
              title={t('makeOwner')}
              clickableTitle
              tierFeature={
                isOwnerForGroup(props.group)
                  ? TierFeatures.ASSIGN_EVENT_OWNERSHIP
                  : undefined
              }
              neededTier={
                isOwnerForGroup(props.group)
                  ? featureToMinimumTierNeeded(
                      TierFeatures.ASSIGN_EVENT_OWNERSHIP,
                      amountOfAdminsInGroup(props.group)
                    )
                  : undefined
              }
              action={() => {
                performAction(async () => {
                  if (props.eventHook) {
                    props.eventHook.setOwnerId(props.participantId)
                  }
                }, props)
              }}
            />
          </MenuItem>
        </div>
      ) : (
        <>
          <MenuItem sx={{ padding: 0 }}>
            <Color {...props} />
          </MenuItem>
          {memberHasColor(props) ? (
            <MenuItem>
              <RemoveColor {...props} />
            </MenuItem>
          ) : (
            <></>
          )}
          {LocalStorage.get(LocalStorageKey.USER_ID) === props.participantId ||
          props.participantId === props.group.OwnerID ? (
            <></>
          ) : (
            <>
              {isConvertedGroup(props.group) ? (
                <></>
              ) : (
                <>
                  <MenuItem>
                    <AdminPermissions {...props} />
                  </MenuItem>
                </>
              )}
            </>
          )}
          {LocalStorage.get(LocalStorageKey.USER_ID) === props.participantId ||
          props.participantId === props.group.OwnerID ? (
            <></>
          ) : (
            <>
              <Divider sx={{ borderColor: 'rgb(230,230,230)' }} />
              <MenuItem>
                <RemoveFromCalendar {...props} />
              </MenuItem>
            </>
          )}
          {canTransferOwnership ? (
            <MenuItem>
              <GroupOwnership {...props} />
            </MenuItem>
          ) : (
            <></>
          )}
        </>
      )}
    </>
  )
}

function memberHasColor(props: ParticipantsActionsPopupContentProps): boolean {
  return props.group.Participants[props.participantId]?.MemberColor?.length > 0
}

function GroupOwnership(props: ParticipantsActionsPopupContentProps) {
  const [showApproveDialog, setShowApproveDialog] = useState(false)

  return (
    <>
      <PopupSectionRow
        title={t('transferCalendarOwnership')}
        onlyTitle
        clickableTitle
        titleColor="red"
        action={() => {
          setShowApproveDialog(true)
        }}
      />
      {showApproveDialog && (
        <ConfirmationDialog
          title={t('transferCalendarOwnershipTitle')}
          content={t('transferCalendarOwnershipDesc', {
            name: isTrulyEmpty(props.participantInfo?.FullName)
              ? props.participantInfo?.PhoneNumber
              : props.participantInfo?.FullName
          })}
          handleRightButton={() => {
            performAction(async () => {
              await transferOwnership(
                props.group._id ?? '',
                props.participantId
              )
            }, props)
          }}
          handleLeftButton={() => {
            setShowApproveDialog(false)
          }}
          handleClose={() => {
            setShowApproveDialog(false)
          }}
          leftButtonColor="black"
          rightButton={t('transferOwnership')}
          leftButton={t('cancel')}
          rightButtonColor={APP_COLOR}
          open={true}
        />
      )}
    </>
  )
}

function RemoveColor(props: ParticipantsActionsPopupContentProps) {
  const connection = useConnection()
  const removeColor = () => {
    if (connection.isConnectionAvailable())
      performAction(() => {
        return changeParticipantColor(
          props.participant,
          '',
          props.participantId,
          props.group
        )
      }, props)
  }
  return (
    <PopupSectionRow
      clickableTitle
      onlyTitle
      title={t('removeColor')}
      action={removeColor}
    />
  )
}

function Color(props: ParticipantsActionsPopupContentProps) {
  const connection = useConnection()
  const assignColor = function (color: string): void {
    if (connection.isConnectionAvailable())
      performAction(() => {
        return changeParticipantColor(
          props.participant,
          color,
          props.participantId,
          props.group
        )
      }, props)
  }
  const [showColorPicker, setShowColorPicker] = useState(false)

  let neededTier = featureToMinimumTierNeeded(
    TierFeatures.MEMBERCOLOR,
    amountOfAdminsInGroup(props.group)
  )

  if (!isOwnerForGroup(props.group)) neededTier = Tier.FREE

  return (
    <ColorsPopup
      open={showColorPicker}
      color={''}
      setOpen={setShowColorPicker}
      setColor={assignColor}
      selector={
        <div
          style={{
            color: theme.palette.text.primary,
            fontSize: 14,
            cursor: 'pointer',
            width: '100%',
            margin: theme.spacing(2, 4)
          }}
        >
          <PopupSectionRow
            action={() => {
              setShowColorPicker(true)
            }}
            clickableTitle
            tierFeature={TierFeatures.MEMBERCOLOR}
            neededTier={neededTier}
            title={memberHasColor(props) ? t('updateColor') : t('assignColor')}
          />
        </div>
      }
    />
  )
}

function RemoveFromCalendar(props: ParticipantsActionsPopupContentProps) {
  const connection = useConnection()
  const removeFromCalendar = () => {
    if (connection.isConnectionAvailable())
      performAction(() => {
        const data = buildRemoveParticipantData(
          props.participant,
          props.participantId,
          props.group._id ?? ''
        )
        return updateGroupOnServer(data, props.group)
      }, props)
  }
  return (
    <PopupSectionRow
      title={`${t('removeFromSharedCalendar')}`}
      titleColor="red"
      clickableTitle
      action={removeFromCalendar}
    />
  )
}

function AdminPermissions(props: ParticipantsActionsPopupContentProps) {
  const isAdmin = props.participant?.Type === GroupParticipantType.ADMIN
  const title = isAdmin ? t('removeAdminPermissions') : t('makeAdmin')
  const connection = useConnection()

  const toggleAdminPermissions = () => {
    if (connection.isConnectionAvailable())
      performAction(() => {
        const newType = isAdmin
          ? GroupParticipantType.REGULAR
          : GroupParticipantType.ADMIN

        props.setLoading(true)
        return changeParticipantAdminStatus(
          props.participant,
          newType,
          props.participantId,
          props.group
        )
      }, props)
  }

  let neededTier = featureToMinimumTierNeeded(
    TierFeatures.ADMINS,
    amountOfAdminsInGroup(props.group)
  )

  if (!isOwnerForGroup(props.group)) neededTier = Tier.FREE

  return (
    <PopupSectionRow
      tierFeature={TierFeatures.ADMINS}
      neededTier={neededTier}
      title={title}
      action={toggleAdminPermissions}
      onlyTitle
      clickableTitle
    />
  )
}

async function performAction(
  action: () => Promise<void>,
  props: ParticipantsActionsPopupContentProps
) {
  props.setLoading(true)
  try {
    await action()
  } catch (e) {
    Logger.red(String(e))
    props.setError(parseErrorToMessage(e))
  }
  props.setLoading(false)
  props.setIsOpen(false)
}

export interface ParticipantsActionsPopupArgs {
  children: ReactNode
  isOpen: boolean
  participant: GroupParticipant
  setLoading: (b: boolean) => void
  setIsOpen: (b: boolean) => void
  group: Model<GroupDoc> | GroupDoc
  participantId: string
  setError: (error: string) => void
  actionsOnEvent?: boolean
  eventHook?: IEventDetails
}

export default function ParticipantsActionsPopup(
  props: ParticipantsActionsPopupArgs
) {
  return (
    <Popup
      open={props.isOpen}
      setOpen={props.setIsOpen}
      placement="right"
      content={<ParticipantsActionsPopupContent {...props} />}
    >
      {props.children}
    </Popup>
  )
}
